import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import {
  Backdrop,
  ColumnContainer,
  FULL_SCREEN_HEADER_HEIGHT,
  InfoGrid,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { CaseQuery } from 'dashboard/models/case-query';
import { CaseDataManagerSchema, CaseTemplateAttributes } from 'dashboard/models/case-template';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { Environment } from 'dashboard/models/environment';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import { Workflow } from 'dashboard/models/workflow';
import { FormAddCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormAddCaseQuery';
import { FormEditCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormEditCaseQuery';
import FormCaseTemplateSettings, {
  CaseTemplateSettingsFormData,
} from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings';
import Card from 'design-system/components/Card';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { SessionStorageKey } from 'lib/clients/StorageClient';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { FieldSchema } from 'lib/fields';
import { useSessionStorageMap } from 'lib/hooks/useSessionStorageMap';

export enum CaseTemplateSettingsTabModule {
  General = 'general',
  Advanced = 'advanced',
  Fields = 'fields',
  Queries = 'queries',
}

export const RIGHT_COLUMN_PORTAL_ID = 'account-type-portal';

const StyledBackdrop = styled(Backdrop)`
  padding: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
  padding: ${Spacing[7]} ${Spacing[5]};
  overflow: auto;
`;

const StyledMainColumn = styled(MainColumn)`
  flex: 1 0 710px;
`;

const StyledTabBar = styled(TabBar)`
  margin: 0 ${Spacing[5]} ${Spacing[4]} ${Spacing[4]};
`;

const PortalContainer = styled.div`
  position: absolute;
  right: 0;
  top: ${FULL_SCREEN_HEADER_HEIGHT}px;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
`;

interface CaseTemplateSettingsProps {
  caseTemplateId: string;
  caseTemplateAttributes: CaseTemplateAttributes;
  caseDataManagerSchema: CaseDataManagerSchema;
  currentEnvironment: Environment;
  initialFieldSchema: FieldSchema[];
  tags: Tag[];
  users: User[];
  actionWorkflows: Workflow[];
  dataSources: ExternalIntegrationDataSource[];
  caseVariableDefinitions: CaseVariableDefinition[];
  caseQueries: CaseQuery[];
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
  updateCaseTemplate: (formData: CaseTemplateSettingsFormData) => void;
  handleCreateCaseQuery: (values: FormAddCaseQueryData) => Promise<void>;
  handleUpdateCaseQuery: (values: FormEditCaseQueryData, id: string) => Promise<void>;
}

const CaseTemplateSettings = ({
  caseTemplateId,
  caseTemplateAttributes,
  caseDataManagerSchema,
  currentEnvironment,
  initialFieldSchema,
  tags,
  users,
  actionWorkflows,
  caseVariableDefinitions,
  caseQueries,
  dataSources,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
  updateCaseTemplate,
  handleCreateCaseQuery,
  handleUpdateCaseQuery,
}: CaseTemplateSettingsProps) => {
  const [tabSelected, setTabSelected] = useSessionStorageMap<CaseTemplateSettingsTabModule>(
    SessionStorageKey.CaseTemplateSettingTabIdx,
    caseTemplateId,
    CaseTemplateSettingsTabModule.General,
  );

  return (
    <>
      <StyledBackdrop data-test='case-template-settings'>
        <Container>
          <StyledMainColumn>
            <StyledTabBar>
              <Tab
                id='case-template-settings__tab-general'
                selected={tabSelected === CaseTemplateSettingsTabModule.General}
                onClick={() => {
                  setTabSelected(CaseTemplateSettingsTabModule.General);
                }}
              >
                General
              </Tab>
              <Tab
                id='case-template-settings__tab-fields'
                selected={tabSelected === CaseTemplateSettingsTabModule.Fields}
                onClick={() => {
                  setTabSelected(CaseTemplateSettingsTabModule.Fields);
                }}
              >
                Fields
              </Tab>
              <FeatureFlagged featureFlag='dashboard:cases-queries-editor'>
                <Tab
                  id='case-template-settings__tab-queries'
                  selected={tabSelected === CaseTemplateSettingsTabModule.Queries}
                  onClick={() => {
                    setTabSelected(CaseTemplateSettingsTabModule.Queries);
                  }}
                >
                  Queries
                </Tab>
              </FeatureFlagged>
              <Tab
                id='case-template-settings__tab-advanced'
                selected={tabSelected === CaseTemplateSettingsTabModule.Advanced}
                onClick={() => {
                  setTabSelected(CaseTemplateSettingsTabModule.Advanced);
                }}
              >
                Advanced
              </Tab>
            </StyledTabBar>
            <ColumnContainer>
              <MainColumn>
                <FormCaseTemplateSettings
                  currentEnvironment={currentEnvironment}
                  initialFieldSchema={initialFieldSchema}
                  tags={tags}
                  users={users}
                  actionWorkflows={actionWorkflows}
                  caseDataManagerSchema={caseDataManagerSchema}
                  dataSources={dataSources}
                  caseTemplateId={caseTemplateId}
                  caseVariableDefinitions={caseVariableDefinitions}
                  caseQueries={caseQueries}
                  updateCaseTemplate={updateCaseTemplate}
                  hasCasesBasic={hasCasesBasic}
                  hasCasesEnhanced={hasCasesEnhanced}
                  hasCasesEnterprise={hasCasesEnterprise}
                  handleCreateCaseQuery={handleCreateCaseQuery}
                  handleUpdateCaseQuery={handleUpdateCaseQuery}
                  module={tabSelected}
                />
              </MainColumn>
              <RightColumn>
                <CaseTemplateInfoCard
                  caseTemplateId={caseTemplateId}
                  updatedAt={caseTemplateAttributes['updated-at']}
                />
              </RightColumn>
            </ColumnContainer>
          </StyledMainColumn>
        </Container>
      </StyledBackdrop>
      <PortalContainer id={RIGHT_COLUMN_PORTAL_ID} />
    </>
  );
};

interface CaseTemplateInfoCardProps {
  caseTemplateId: string;
  updatedAt: string;
}

const CaseTemplateInfoCard = ({ caseTemplateId, updatedAt }: CaseTemplateInfoCardProps) => {
  return (
    <Card data-test='case-template-settings__case-template-info-card'>
      <Card.Body>
        <Card.Title>Info</Card.Title>
        <InfoGrid>
          <AttributeItem
            title='Case template ID'
            value={caseTemplateId}
            isRedacted={false}
            placeholder=''
            wordBreak='break-all'
          />
          <AttributeItem
            title='Last updated at'
            value={DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)}
            isRedacted={false}
            placeholder=''
            wordBreak='break-all'
            dataTest='inquiry-template-info__updated-at'
          />
        </InfoGrid>
      </Card.Body>
    </Card>
  );
};

export default CaseTemplateSettings;
